import React from "react";
import { useTranslation } from "react-i18next";

const RELOAD_ATTEMPTED_KEY = "chunk_reload_attempted";
const CACHE_BUST_PARAM = "_chunkReload";

// A stale index.html (Cloudflare has been seen serving one well past our
// Cache-Control, see docs/... KB — the frontend can't fix that server-side)
// can reference a JS chunk hash a later deploy has already deleted. A plain
// reload refetches the exact same cached index.html and fails again; a
// cache-busting query string is what actually forces a fresh copy — proven
// against the real CDN, not just assumed.
export const bustCacheAndReloadUrl = (href: string): string => {
	const url = new URL(href);
	url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString());
	return url.toString();
};

const ChunkLoadFailed = () => {
	const { t } = useTranslation();
	return (
		<div
			className="flex flex-1 flex-col items-center justify-center gap-[12px] px-[24px] text-center"
			style={{ height: "var(--vh)" }}
		>
			<p className="text-text-primary dark:text-text-dark">
				{t("common-components.chunk-load-error.title")}
			</p>
			<button
				type="button"
				className="rounded-[8px] bg-primary px-[16px] py-[8px] text-white"
				onClick={() => {
					// A plain reload would very likely hit the exact same stale response
					// that already failed once automatically. Clearing the guard and
					// busting the cache again gives a manual click the same real chance
					// the automatic first attempt had, rather than a weaker retry.
					sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY);
					window.location.replace(bustCacheAndReloadUrl(window.location.href));
				}}
			>
				{t("common-components.chunk-load-error.reload-button")}
			</button>
		</div>
	);
};

const lazyWithReload = <P extends object>(
	importFn: () => Promise<{ default: React.ComponentType<P> }>,
) =>
	React.lazy(() =>
		importFn().catch(() => {
			// In dev a failed import means the Vite server is down; its client reloads the tab
			// once the server is back, and navigating away here would cut that polling short.
			if (import.meta.env.DEV) {
				return { default: ChunkLoadFailed as React.ComponentType<P> };
			}
			// One retry per tab session: enough to defeat a stale CDN cache without
			// risking a reload loop for someone genuinely offline or on a broken
			// build.
			if (!sessionStorage.getItem(RELOAD_ATTEMPTED_KEY)) {
				sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, "1");
				window.location.replace(bustCacheAndReloadUrl(window.location.href));
				return { default: (() => null) as React.ComponentType<P> };
			}
			return { default: ChunkLoadFailed as React.ComponentType<P> };
		}),
	);

export default lazyWithReload;
